import { Loader2, Phone, Save, ShieldAlert } from "lucide-react";
import { useEffect, useState } from "react";

import { CarregandoBloco, CarregandoFormulario } from "@/components/carregando";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

import { alertasApi } from "./api";
import SecaoListaCompras from "./components/SecaoListaCompras";
import SecaoTiposAlerta from "./components/SecaoTiposAlerta";
import SwitchLinha from "./components/SwitchLinha";
import { doSalvo, mascara, paraSalvar, telefoneValido } from "./lib/telefone";

/** Configuracoes > Sistema > Controle: avisos ao responsavel pelo WhatsApp.
 *  Tres blocos: numero + switch geral; quais alertas receber (um switch por
 *  tipo, inclusive "aviso de estoque no minimo"); lista de compras agendada. */
export default function ControleTab({ toast }) {
  const [dados, setDados] = useState(null);
  const [telefone, setTelefone] = useState("");
  const [erro, setErro] = useState("");
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    alertasApi.carregar()
      .then((d) => { setDados(d); setTelefone(doSalvo(d.phone)); })
      .catch((e) => setErro(e.message));
  }, []);

  if (erro && !dados) return <div className="max-w-3xl mx-auto p-6 text-destructive">{erro}</div>;
  // Carregamento com cards (25/10/2026): os três blocos piscam até a config chegar.
  if (!dados) {
    return (
      <div className="max-w-3xl mx-auto space-y-6">
        <CarregandoFormulario campos={2} />
        <CarregandoBloco altura="h-72" />
        <CarregandoBloco altura="h-40" />
      </div>
    );
  }

  const valido = telefoneValido(telefone);
  const ligarGeral = (x) => {
    if (x && !valido) { setErro("Preencha um telefone válido antes de ativar."); return; }
    setErro(""); setDados({ ...dados, enabled: x });
  };

  const salvar = async () => {
    if (dados.enabled && !valido) { setErro("Telefone inválido. Use o formato (34) 99999-9999."); return; }
    setSalvando(true);
    try {
      const d = await alertasApi.salvar({ enabled: dados.enabled, phone: paraSalvar(telefone), prefs: dados.prefs, lista_compras: dados.lista_compras });
      setDados(d); setTelefone(doSalvo(d.phone)); setErro("");
      toast({ title: "Configuração salva!", description: d.enabled ? "Os avisos no WhatsApp estão ativos." : "Os avisos no WhatsApp estão desativados." });
    } catch (e) {
      setErro(e.message); toast({ title: "Erro ao salvar", description: e.message, variant: "destructive" });
    } finally { setSalvando(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-e2e="controle-tab">
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 rounded-lg bg-primary text-primary-foreground"><ShieldAlert className="w-5 h-5" /></div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold">Aviso via WhatsApp</h2>
            <p className="text-sm text-muted-foreground">Mensagens diretas no seu WhatsApp sobre o que acontece no estabelecimento. Útil para acompanhar a operação de longe.</p>
          </div>
        </div>
        <div className="space-y-3">
          <SwitchLinha testId="sw-geral" titulo="Avisos ativos" descricao="Quando ativo, as mensagens vão para o número abaixo." ligado={!!dados.enabled} onChange={ligarGeral} />
          <div className="space-y-2">
            <label className="text-sm font-medium flex items-center gap-2"><Phone className="w-4 h-4" /> Telefone do responsável</label>
            <Input value={telefone} onChange={(e) => { setTelefone(mascara(e.target.value)); setErro(""); }} placeholder="(34) 99999-9999" inputMode="tel" className="h-11 max-w-xs" data-e2e="telefone" />
            {erro && <p className="text-xs text-destructive" data-e2e="erro">{erro}</p>}
          </div>
        </div>
      </div>

      <SecaoTiposAlerta tipos={dados.tipos} prefs={dados.prefs} onChange={(prefs) => setDados({ ...dados, prefs })} />
      <SecaoListaCompras valor={dados.lista_compras} onChange={(lista_compras) => setDados({ ...dados, lista_compras })} />

      <div className="flex justify-end">
        <Button onClick={salvar} disabled={salvando} className="h-11 px-6" data-e2e="salvar-controle">
          {salvando ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
          Salvar configurações
        </Button>
      </div>
    </div>
  );
}
